import { useRef } from "react";
import {
  MasonryProps,
  MasonryScroller,
  useContainerPosition,
  useInfiniteLoader,
  usePositioner,
  useResizeObserver,
} from "masonic";
import { useWindowSize } from "@react-hook/window-size";
import { useMediaQuery } from "../../foundations/useMediaQuery";

export default function InfiniteMasonry<T extends { _id?: string }>({
  items,
  onFetchMore,
  limit,
  component,
  itemKey,
  maxColumnCount = 5,
  rowGutter = 6,
  columnGutter = 6,
  overscanBy = 2,
}: {
  items: { nextCursor?: string | null; data: T[] };
  onFetchMore?: (cursor?: string | null, limit?: number) => Promise<unknown>;
  limit: number;
  itemKey: (x: T) => string | number;
  component: (props: { data: T; index: number }) => JSX.Element | null;
  maxColumnCount?: number;
  rowGutter?: number;
  columnGutter?: number;
  overscanBy?: number;
}) {
  const maybeLoadMore = useInfiniteLoader(
    () => {
      if (onFetchMore && items.nextCursor !== null) {
        return onFetchMore(items.nextCursor, limit);
      }
    },
    {
      threshold: limit,
      isItemLoaded: (index, data) => !!data[index],
    },
  );

  return (
    <Masonic
      onRender={onFetchMore ? maybeLoadMore : undefined}
      items={items.data}
      itemKey={itemKey}
      maxColumnCount={maxColumnCount}
      rowGutter={rowGutter}
      columnGutter={columnGutter}
      overscanBy={overscanBy}
      render={component}
    />
  );
}

function Masonic<Item extends { _id?: string }>(props: MasonryProps<Item>) {
  const containerRef = useRef(null);
  const [windowWidth, windowHeight] = useWindowSize();
  const { offset, width } = useContainerPosition(containerRef, [
    windowWidth,
    windowHeight,
  ]);
  const isMobile = useMediaQuery("sm");

  const lengthRef = useRef(0);
  const positioner = usePositioner(
    {
      width: width || (isMobile ? windowWidth - 24 : windowWidth - 295),
      columnWidth: props.columnWidth,
      columnGutter: props.columnGutter,
      rowGutter: props.rowGutter,
      columnCount: props.columnCount,
      maxColumnCount: props.maxColumnCount,
    },
    [lengthRef.current > props.items.length, props.items[0]?._id],
  );
  const resizeObserver = useResizeObserver(positioner);
  lengthRef.current = props.items.length;

  return (
    <MasonryScroller
      positioner={positioner}
      resizeObserver={resizeObserver}
      containerRef={containerRef}
      items={props.items}
      onRender={props.onRender}
      itemKey={props.itemKey}
      height={windowHeight}
      offset={offset}
      overscanBy={props.overscanBy}
      render={props.render}
    />
  );
}
